// Text catalog for the Karriere / Careers page. Images live in
// src/pages/KarrierePage.tsx.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type Stat = { value: string; label: string };
type Quote = { text: string; name: string; role: string };

type KarriereStrings = {
  meta: PageMeta;
  hero: { title: string; p1: string; p2: string };
  team: { heading: string; p1: string; p2: string; p3: string };
  stats: Stat[];
  quote1: Quote;
  local: { heading: string; p1: string; p2: string; imgAlt1: string; imgAlt2: string };
  insight: { heading: string; p1: string; p2: string };
  quote2: Quote;
  wellbeing: { heading: string; p1: string; p2: string; initiatives: string[]; p3: string; p4: string; imgAlt: string };
  join: { heading: string; p1: string; p2: string; cta: string };
};

// External Emply job portal links are NOT localized (kept verbatim per source).
export const EMPLY_JOBS_URL = "https://misupport.career.emply.com/da/ledige-stillinger";

export const karriereStrings: Record<Locale, KarriereStrings> = {
  da: {
    meta: {
      title: "MI Support som arbejdsplads - Karriere",
      description:
        "Som virksomhed er MI Support baseret på vores handlekraftige, ansvarlige og motiverede medarbejdere. Lær os bedre at kende og se vores ledige stillinger.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/01.jpeg",
    },
    hero: {
      title: "MI Support som arbejdsplads",
      p1: "Som virksomhed er MI Support baseret på vores handlekraftige, ansvarlige og motiverede medarbejdere, der med faglig stolthed arbejder målrettet og engageret for at løse vores kunders udfordringer.",
      p2: "Lær os bedre at kende her.",
    },
    team: {
      heading: "Medarbejderne bag MI Support",
      p1: "MI Support består i dag af +45 erfarne konsulenter i alle aldersgrupper og med mange forskellige personlige, faglige og uddannelsesmæssige baggrunde.",
      p2: "Med diversitet som en af vores kerneværdier, værdsætter vi i høj grad forskellighederne blandt vores medarbejdere, og vi stræber efter at fremme en inkluderende kultur, hvor der er plads til alle.",
      p3: "Vores team omfatter især ingeniører, bygningskonstruktører, maskinmestre samt medarbejdere med erhvervsfaglige uddannelser, der alle bringer mange års erfaring fra store byggeprojekter med sig.",
    },
    stats: [
      { value: "+45", label: "Antal medarbejdere i virksomheden" },
      { value: "23 år", label: "Alder på yngste medarbejder" },
      { value: "64 år", label: "Alder på ældste medarbejder" },
    ],
    quote1: {
      text: "Vi stræber efter at have kvalificerede medarbejdere med et højt fagligt niveau. Derfor prioriterer vi kontinuerlig kompetenceudvikling og tilbyder videreuddannelse for at sikre, at vores medarbejdere altid er på forkant med de nyeste metoder og teknologier inden for branchen.",
      name: "Sonny Christensen",
      role: "Ejer & stifter af MI Support",
    },
    local: {
      heading: "Lokal tilstedeværelse og central støtte",
      p1: "Vores medarbejdere er geografisk spredt udover mange forskellige adresser, hovedsageligt på Sjælland, hvor de indgår som en del af vores kunders projektorganisationer.",
      p2: "Dertil fungerer MI Supports hovedkontor i Greve som et centralt knudepunkt, hvor vores administration støtter op om projekterne og kan assistere inden for en lang række fagområder, så alle kan udføre deres arbejde bedst muligt.",
      imgAlt1: "Hovedkontor i Greve",
      imgAlt2: "Kollegaer på kontoret",
    },
    insight: {
      heading: "Projektindsigt og faglig sparring",
      p1: "Selvom MI Support er en ejerledet virksomhed, har vi en flad organisationsstruktur, hvor disse ledende medarbejdere selv har daglig gang ude på projekterne.",
      p2: "Udover korte kommunikationsveje og hurtige beslutningsprocesser betyder det, at lederkredsen har dyb indsigt i de udfordringer, der kan opstå undervejs i byggeprocesserne, og kan byde ind med solid faglig sparring.",
    },
    quote2: {
      text: "At være en del af MI Support handler ikke kun om at levere resultater, det handler også om at være en del af et fællesskab, hvor udvikling og samarbejde går hånd i hånd.",
      name: "Kristine Liljeberg",
      role: "Head of Operations i MI Support",
    },
    wellbeing: {
      heading: "Fokus på trivsel og sammenhold",
      p1: "I MI Support tror vi på, at stærke og langvarige relationer, både med kunder og internt i organisationen, er afgørende for en bæredygtig forretning. Derfor lægger vi stor vægt på at skabe et positivt og udviklende arbejdsmiljø, hvor vores medarbejdere føler sig som en vigtig del af vores sociale og faglige fællesskab.",
      p2: "Vi arbejder målrettet på at fremme trivsel, faglig sparring og sammenhold gennem initiativer som:",
      initiatives: [
        "Hyppige faglige events og workshops",
        "Sociale aktiviteter som fredagsbarer, fester og teambuilding",
        "Fokus på individuel og kollektiv kompetenceudvikling",
      ],
      p3: "At være en del af MI Support handler ikke kun om at levere resultater, det handler også om at være en del af et fællesskab, hvor udvikling og samarbejde går hånd i hånd.",
      p4: "Vi tror på, at et godt arbejdsmiljø og glade medarbejdere er nøglen til vores fortsatte succes.",
      imgAlt: "Kollegaer til socialt arrangement",
    },
    join: {
      heading: "Er du vores nye kollega?",
      p1: "I MI Support er vi altid på udkig efter nye, dygtige kollegaer, der har lyst til at blive en del af vores team. Vi har jævnligt ledige stillinger slået op, og ellers er du altid velkommen til at skrive dig op i vores CV-bank, som vi løbende screener for interessante profiler.",
      p2: "MI Support er en dynamisk arbejdsplads, hvor der ikke er langt fra tanke til handling. Vi tilbyder engagerede og gode kollegaer med fokus på udvikling og faglig sparring. Herudover har vi gode og stabile arbejdsvilkår, og vi lægger vægt på et rart arbejdsmiljø med hyppige sociale arrangementer.",
      cta: "Se vores jobmuligheder",
    },
  },

  en: {
    meta: {
      title: "MI Support as a workplace - Careers",
      description:
        "As a company, MI Support is built on our proactive, responsible and motivated employees. Get to know us better and see our open positions.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/01.jpeg",
    },
    hero: {
      title: "MI Support as a workplace",
      p1: "As a company, MI Support is built on our proactive, responsible and motivated employees who, with professional pride, work in a focused and committed way to solve our customers' challenges.",
      p2: "Get to know us better here.",
    },
    team: {
      heading: "The people behind MI Support",
      p1: "MI Support today consists of 45+ experienced consultants in all age groups and with many different personal, professional and educational backgrounds.",
      p2: "With diversity as one of our core values, we greatly appreciate the differences among our employees, and we strive to foster an inclusive culture where there is room for everyone.",
      p3: "Our team includes in particular engineers, architectural technologists, marine engineers and employees with vocational educations, all of whom bring many years of experience from large construction projects.",
    },
    stats: [
      { value: "+45", label: "Number of employees in the company" },
      { value: "23 years", label: "Age of the youngest employee" },
      { value: "64 years", label: "Age of the oldest employee" },
    ],
    quote1: {
      text: "We strive to have qualified employees with a high professional level. We therefore prioritise continuous competence development and offer further education to ensure that our employees are always at the forefront of the latest methods and technologies in the industry.",
      name: "Sonny Christensen",
      role: "Owner & founder of MI Support",
    },
    local: {
      heading: "Local presence and central support",
      p1: "Our employees are geographically spread across many different addresses, mainly on Zealand, where they form part of our customers' project organisations.",
      p2: "In addition, MI Support's head office in Greve serves as a central hub, where our administration supports the projects and can assist within a wide range of disciplines, so that everyone can do their work as well as possible.",
      imgAlt1: "Head office in Greve",
      imgAlt2: "Colleagues at the office",
    },
    insight: {
      heading: "Project insight and professional sparring",
      p1: "Although MI Support is an owner-managed company, we have a flat organisational structure, where these leading employees are themselves out on the projects on a daily basis.",
      p2: "In addition to short lines of communication and fast decision-making, this means that the management circle has deep insight into the challenges that can arise during the construction processes, and can contribute solid professional sparring.",
    },
    quote2: {
      text: "Being part of MI Support is not only about delivering results, it is also about being part of a community where development and collaboration go hand in hand.",
      name: "Kristine Liljeberg",
      role: "Head of Operations at MI Support",
    },
    wellbeing: {
      heading: "Focus on well-being and togetherness",
      p1: "At MI Support, we believe that strong and lasting relationships, both with customers and internally in the organisation, are crucial for a sustainable business. We therefore place great emphasis on creating a positive and developing working environment, where our employees feel like an important part of our social and professional community.",
      p2: "We work purposefully to promote well-being, professional sparring and togetherness through initiatives such as:",
      initiatives: [
        "Frequent professional events and workshops",
        "Social activities such as Friday bars, parties and team building",
        "Focus on individual and collective competence development",
      ],
      p3: "Being part of MI Support is not only about delivering results, it is also about being part of a community where development and collaboration go hand in hand.",
      p4: "We believe that a good working environment and happy employees are the key to our continued success.",
      imgAlt: "Colleagues at a social event",
    },
    join: {
      heading: "Are you our new colleague?",
      p1: "At MI Support we are always on the lookout for new, talented colleagues who would like to become part of our team. We regularly post open positions, and otherwise you are always welcome to add yourself to our CV bank, which we continuously screen for interesting profiles.",
      p2: "MI Support is a dynamic workplace, where there is a short distance from thought to action. We offer committed and good colleagues with a focus on development and professional sparring. In addition, we have good and stable working conditions, and we place emphasis on a pleasant working environment with frequent social events.",
      cta: "See our job opportunities",
    },
  },

  de: {
    meta: {
      title: "MI Support als Arbeitsplatz - Karriere",
      description:
        "MI Support baut auf tatkräftige, verantwortungsbewusste und motivierte Mitarbeiter. Lernen Sie uns kennen und sehen Sie unsere offenen Stellen.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/01.jpeg",
    },
    hero: {
      title: "MI Support als Arbeitsplatz",
      p1: "Als Unternehmen baut MI Support auf unsere tatkräftigen, verantwortungsbewussten und motivierten Mitarbeiter, die mit fachlichem Stolz zielgerichtet und engagiert daran arbeiten, die Herausforderungen unserer Kunden zu lösen.",
      p2: "Lernen Sie uns hier besser kennen.",
    },
    team: {
      heading: "Die Menschen hinter MI Support",
      p1: "MI Support besteht heute aus über 45 erfahrenen Beratern aller Altersgruppen und mit vielen unterschiedlichen persönlichen, fachlichen und bildungsmäßigen Hintergründen.",
      p2: "Da Diversität einer unserer Kernwerte ist, schätzen wir die Unterschiede unter unseren Mitarbeitern sehr und streben danach, eine inklusive Kultur zu fördern, in der für alle Platz ist.",
      p3: "Unser Team umfasst insbesondere Ingenieure, Bautechniker, Schiffsingenieure sowie Mitarbeiter mit berufsfachlicher Ausbildung, die alle viele Jahre Erfahrung aus großen Bauprojekten mitbringen.",
    },
    stats: [
      { value: "+45", label: "Anzahl der Mitarbeiter im Unternehmen" },
      { value: "23 Jahre", label: "Alter des jüngsten Mitarbeiters" },
      { value: "64 Jahre", label: "Alter des ältesten Mitarbeiters" },
    ],
    quote1: {
      text: "Wir streben danach, qualifizierte Mitarbeiter mit einem hohen fachlichen Niveau zu haben. Daher legen wir Wert auf kontinuierliche Kompetenzentwicklung und bieten Weiterbildung an, um sicherzustellen, dass unsere Mitarbeiter stets bei den neuesten Methoden und Technologien der Branche an vorderster Front sind.",
      name: "Sonny Christensen",
      role: "Inhaber & Gründer von MI Support",
    },
    local: {
      heading: "Lokale Präsenz und zentrale Unterstützung",
      p1: "Unsere Mitarbeiter sind geografisch über viele verschiedene Standorte verteilt, hauptsächlich auf Seeland, wo sie Teil der Projektorganisationen unserer Kunden sind.",
      p2: "Darüber hinaus dient die Hauptniederlassung von MI Support in Greve als zentraler Knotenpunkt, an dem unsere Verwaltung die Projekte unterstützt und in einer Vielzahl von Fachbereichen assistieren kann, damit alle ihre Arbeit bestmöglich erledigen können.",
      imgAlt1: "Hauptniederlassung in Greve",
      imgAlt2: "Kollegen im Büro",
    },
    insight: {
      heading: "Projekteinblick und fachliches Sparring",
      p1: "Obwohl MI Support ein inhabergeführtes Unternehmen ist, haben wir eine flache Organisationsstruktur, in der diese leitenden Mitarbeiter selbst täglich draußen auf den Projekten sind.",
      p2: "Neben kurzen Kommunikationswegen und schnellen Entscheidungsprozessen bedeutet das, dass der Führungskreis tiefen Einblick in die Herausforderungen hat, die im Verlauf der Bauprozesse entstehen können, und mit solidem fachlichem Sparring beitragen kann.",
    },
    quote2: {
      text: "Ein Teil von MI Support zu sein, bedeutet nicht nur, Ergebnisse zu liefern, sondern auch, Teil einer Gemeinschaft zu sein, in der Entwicklung und Zusammenarbeit Hand in Hand gehen.",
      name: "Kristine Liljeberg",
      role: "Head of Operations bei MI Support",
    },
    wellbeing: {
      heading: "Fokus auf Wohlbefinden und Zusammenhalt",
      p1: "Bei MI Support glauben wir, dass starke und langfristige Beziehungen – sowohl mit Kunden als auch intern in der Organisation – entscheidend für ein nachhaltiges Geschäft sind. Daher legen wir großen Wert darauf, ein positives und förderndes Arbeitsumfeld zu schaffen, in dem sich unsere Mitarbeiter als wichtiger Teil unserer sozialen und fachlichen Gemeinschaft fühlen.",
      p2: "Wir arbeiten zielgerichtet daran, Wohlbefinden, fachliches Sparring und Zusammenhalt durch Initiativen wie diese zu fördern:",
      initiatives: [
        "Häufige fachliche Events und Workshops",
        "Soziale Aktivitäten wie Freitagsbars, Feste und Teambuilding",
        "Fokus auf individuelle und kollektive Kompetenzentwicklung",
      ],
      p3: "Ein Teil von MI Support zu sein, bedeutet nicht nur, Ergebnisse zu liefern, sondern auch, Teil einer Gemeinschaft zu sein, in der Entwicklung und Zusammenarbeit Hand in Hand gehen.",
      p4: "Wir glauben, dass ein gutes Arbeitsumfeld und zufriedene Mitarbeiter der Schlüssel zu unserem anhaltenden Erfolg sind.",
      imgAlt: "Kollegen bei einer sozialen Veranstaltung",
    },
    join: {
      heading: "Sind Sie unser neuer Kollege?",
      p1: "Bei MI Support sind wir stets auf der Suche nach neuen, fähigen Kollegen, die Lust haben, Teil unseres Teams zu werden. Wir schreiben regelmäßig offene Stellen aus, und ansonsten sind Sie jederzeit herzlich willkommen, sich in unsere CV-Datenbank einzutragen, die wir laufend nach interessanten Profilen durchsehen.",
      p2: "MI Support ist ein dynamischer Arbeitsplatz, an dem der Weg vom Gedanken zur Handlung kurz ist. Wir bieten engagierte und gute Kollegen mit Fokus auf Entwicklung und fachliches Sparring. Darüber hinaus haben wir gute und stabile Arbeitsbedingungen und legen Wert auf ein angenehmes Arbeitsumfeld mit häufigen sozialen Veranstaltungen.",
      cta: "Sehen Sie unsere Stellenangebote",
    },
  },
};
